import { useEffect } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { api, saveAcquisition } from "@/lib/api";
import { useTour } from "@/components/tour/TourProvider";
import { ArrowRight, Download, Search, FileText, Landmark, BadgeCheck, Building2, MapPin, Flag, PlayCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PricingCards } from "@/components/PricingCards";
import { useAuth } from "@/context/AuthContext";
import { REVIEW_PRICE } from "@/lib/constants";
import { FundingCaseStudy } from "@/components/FundingCaseStudy";
import { SmallBizStories } from "@/components/SmallBizStories";
import { SiteNav, SiteFooter } from "@/components/site/SiteChrome";
import { FounderBlock } from "@/components/site/FounderBlock";
import { CustomerProof } from "@/components/site/CustomerProof";
import { AfterState } from "@/components/site/AfterState";
import { HomeFaq } from "@/components/site/HomeFaq";
import { useSpanishScreens } from "@/lib/i18n";
import { usePageMeta, DEFAULT_META } from "@/lib/seo";

const ES_META = { title: "GrantVerify | Subvenciones, propuestas y licencias para pequeños negocios", description: "GrantVerify ayuda a dueños de pequeños negocios a encontrar subvenciones federales, estatales, de condado y municipales, trazar cada paso, redactar la propuesta y revisarla con un editor humano certificado. Búsqueda gratuita, planes desde $39/mes.", path: "/", image: "og-image-es.png" };

const HERO_IMG = "/brand/product-local-grants.webp";
const EDITOR_IMG = "https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function GuideLink() {
  const { lang } = useTour();
  const es = lang === "es";
  return (
    <a href={`/brand/guides/grantverify-customer-guide-${es ? "es" : "en"}.pdf`} download data-no-translate className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800 underline-offset-4 hover:underline" data-testid="hero-download-guide-link">
      <Download className="h-4 w-4" />{es ? "Descargue la guía: cómo le ayuda GrantVerify (PDF)" : "Download the guide: how GrantVerify helps you (PDF)"}
    </a>
  );
}

function Hero() {
  const { user } = useAuth();
  const { start: startTour, offerTour } = useTour();
  useEffect(() => { if (user === false && window.innerWidth >= 640) { const t = setTimeout(() => offerTour("landing"), 3500); return () => clearTimeout(t); } }, [user, offerTour]);
  return (
    <section className="grid-bg border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-8 pb-16 sm:py-20 lg:py-28">
        <div className="lg:col-span-7 fade-up">
          <div className="eyebrow mb-3 sm:mb-5">Federal · State · County · City</div>
          <h1 className="font-display text-[2rem] leading-[1.08] sm:text-5xl lg:text-6xl font-extrabold tracking-tight sm:leading-[1.05] text-slate-900" data-testid="hero-title">
            Find the grant your small business qualifies for. Write the proposal. <span className="text-emerald-600">Get it verified.</span>
          </h1>
          <p className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="hero-subtitle">Stop spending evenings on agency sites: we search federal, state, county and city programs, map every step, and write the proposal.</p>
          <p className="mt-2 sm:mt-3 text-sm sm:text-base text-slate-800 font-medium max-w-xl leading-relaxed" data-testid="hero-after-state">Walk into every application knowing you qualify, with your licenses in order and a proposal a certified editor has checked.</p>
          <p className="mt-2 sm:mt-3 text-sm sm:text-base text-slate-600 max-w-xl leading-relaxed">Grants are non-dilutive funding: you keep full ownership of your business.</p>
          <div className="mt-6 sm:mt-10 flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/login?mode=register" data-testid="hero-get-started-button">
              <Button className="h-12 px-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-base">
                Start finding grants <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Button variant="ghost" onClick={() => startTour("landing")} className="h-12 px-5 rounded-full text-base text-emerald-700 hover:bg-emerald-50" data-testid="hero-tour-button"><PlayCircle className="mr-2 h-5 w-5" />Take the 60-second tour</Button>
            <a href="#pricing" className="text-base font-medium text-slate-700 underline-offset-4 hover:underline" data-testid="hero-pricing-link">See pricing</a>
          </div>
          <p className="mt-6 text-sm font-medium text-slate-700 flex items-center gap-2" data-testid="hero-free-line"><ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />Grant search is free, no credit card required.</p>
          <p className="mt-1 text-sm text-slate-500" data-testid="hero-trust-line">No guaranteed-funding promises: agencies make every award decision. We don't sell your data.</p>
          <p className="mt-1 text-sm text-slate-500" data-testid="hero-refund-line">14-day money-back guarantee on your first plan. After 14 days all sales are final.</p>
          <GuideLink />
        </div>
        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
          <img src={HERO_IMG} alt="GrantVerify Grant Finder results: Illinois state and Cook County small business programs with amounts, deadlines and match-funding flags" className="rounded-lg border border-slate-200 shadow-xl aspect-[4/5] object-cover object-top w-full bg-white" data-testid="hero-product-screenshot" />
          <div className="absolute -bottom-6 left-2 sm:-left-6 bg-white border border-slate-200 rounded-lg p-4 shadow-lg w-64">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-600"><BadgeCheck className="h-4 w-4" /> Peer Verified</div>
            <div className="mt-2 text-sm text-slate-700">Certified human editor final compliance review.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Search, t: "Search every level", d: "Live Grants.gov listings plus AI research on state, county and city programs." },
  { icon: Flag, t: "Map the steps", d: "Eligibility, registrations, documents and a dated action plan for your industry." },
  { icon: FileText, t: "Generate the proposal", d: "A complete, funder-ready draft you can edit, save and download." },
  { icon: BadgeCheck, t: "Verify with a human", d: "Send it to a certified grant editor for a final compliance check." },
];

function How() {
  return (
    <section id="how" className="scroll-mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <div className="eyebrow mb-3">How it works</div>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 max-w-2xl">From search to submission in one workspace.</h2>
      <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">Walk into the bank, your board or the funder with a plan that has already been checked, instead of guessing whether your business is fundable.</p>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-slate-200">
        {STEPS.map((s, i) => (
          <div key={s.t} className="border-r border-b border-slate-200 p-8 bg-white hover:bg-emerald-50/40 transition-colors">
            <div className="font-mono text-xs text-slate-400">0{i + 1}</div>
            <s.icon className="h-6 w-6 text-emerald-600 mt-6" />
            <h3 className="font-display text-xl font-bold mt-4 text-slate-900">{s.t}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Areas() {
  return (
    <section id="areas" className="scroll-mt-16 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-slate-700 rounded-lg p-10 card-lift">
          <div className="font-mono text-xs tracking-[0.18em] uppercase text-emerald-400">Area 01</div>
          <FileText className="h-8 w-8 mt-6 text-emerald-400" />
          <h3 className="font-display text-3xl font-extrabold mt-4">Proposal Writer</h3>
          <p className="text-slate-300 mt-3 leading-relaxed">Cover letter, statement of need, goals, measurable outcomes, timeline, evaluation, budget justification and sustainability — written from your organization and the grant you choose.</p>
        </div>
        <div className="border border-slate-700 rounded-lg p-10 card-lift">
          <div className="font-mono text-xs tracking-[0.18em] uppercase text-emerald-400">Area 02</div>
          <Landmark className="h-8 w-8 mt-6 text-emerald-400" />
          <h3 className="font-display text-3xl font-extrabold mt-4">State Licensing Guide</h3>
          <p className="text-slate-300 mt-3 leading-relaxed">A step-by-step checklist of the licenses, permits and registrations your industry needs in your state — agencies, fees, timelines and official links.</p>
        </div>
      </div>
    </section>
  );
}

function Review() {
  return (
    <section id="review" className="scroll-mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <img src={EDITOR_IMG} alt="Certified grant editor marking up a printed grant proposal before submission" className="lg:col-span-5 rounded-lg border border-slate-200 aspect-[4/3] object-cover w-full" />
      <div className="lg:col-span-7 border-l-4 border-amber-500 pl-8">
        <div className="font-mono text-xs tracking-[0.18em] uppercase text-amber-600">Final review upsell</div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">A certified human editor checks it before you submit.</h2>
        <p className="mt-4 text-slate-600 leading-relaxed">Send any proposal you generate to the Peer Verify network of certified grant editors for a line-by-line compliance review, suggested edits and a certification stamp.</p>
        <div className="mt-8 flex items-baseline gap-3">
          <span className="font-display text-5xl font-extrabold text-slate-900">${REVIEW_PRICE}</span>
          <span className="text-slate-500">flat fee per proposal · $149 on Full Verify Suite</span>
        </div>
      </div>
    </section>
  );
}

function useShareArrival() {
  const [params] = useSearchParams();
  const { setLang } = useTour();
  useEffect(() => {
    if (params.get("lang") === "es") setLang("es");
    if (!["funding_card", "tv_ad", "paid_ad", "qr_share"].includes(params.get("utm_campaign"))) return;
    const source = ["linkedin", "facebook", "copy", "roku", "youtube", "qr"].includes(params.get("utm_source")) ? params.get("utm_source") : "direct";
    const ad = /^[a-z0-9-]{2,30}$/.test(params.get("utm_content") || "") ? params.get("utm_content") : "";
    saveAcquisition(source, ad);
    if (!sessionStorage.getItem("gv_share_visit")) {
      sessionStorage.setItem("gv_share_visit", "1");
      api.post("/public/share-event", { type: "visit", source, ad, lang: params.get("lang") || "en" }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function Landing() {
  const { lang } = useTour();
  useSpanishScreens(lang === "es");
  usePageMeta(lang === "es" ? ES_META : DEFAULT_META);
  useShareArrival();
  const { hash } = useLocation();
  useEffect(() => { if (!hash) return; const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 300); return () => clearTimeout(t); }, [hash]);
  return (
    <div className="bg-slate-50 min-h-screen">
      <SiteNav home />
      <Hero />
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap gap-x-10 gap-y-3 text-sm text-slate-500">
          <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Live Grants.gov data</span>
          <span className="flex items-center gap-2"><MapPin className="h-4 w-4" /> All 50 states + DC</span>
          <span className="flex items-center gap-2"><BadgeCheck className="h-4 w-4" /> Certified human editors</span>
        </div>
      </div>
      <AfterState />
      <FundingCaseStudy />
      <SmallBizStories />
      <CustomerProof />
      <How />
      <Areas />
      <Review />
      <section id="pricing" className="scroll-mt-16 border-t border-slate-200 grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="eyebrow mb-3">Pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Pick an area, or get both.</h2>
          <p className="mt-4 mb-12 text-slate-600 max-w-2xl" data-testid="pricing-scope-line">Built for small business owners and nonprofits doing their own grant work, not enterprise grant teams.</p>
          <PricingCards />
        </div>
      </section>
      <HomeFaq />
      <FounderBlock />
      <SiteFooter />
    </div>
  );
}
